import React, { useMemo, useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Eye } from "lucide-react";
import { Button } from "react-bootstrap";
import { saveAs } from "file-saver";
import { RiShareForwardLine, RiDownloadFill } from "react-icons/ri";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { HiOutlineMail } from "react-icons/hi";
import { FaArrowLeft } from "react-icons/fa6";
import { SiTicktick } from "react-icons/si";
import { ImConfused } from "react-icons/im";
import { Document, Packer, Paragraph, TextRun } from "docx";
import "../components/Student/StuStyles/StuLiveRegister.css";
import ApiService from "../nhrc_apis/ApiService";

const { JobFairAPI } = ApiService;

function JfTotalApplicationsRes() {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [memberTypeFilter, setMemberTypeFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [nhrcFilter, setNhrcFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [jobTitle, setJobTitle] = useState("Unknown Position");
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (location.state?.jobTitle) setJobTitle(location.state.jobTitle);
  }, [location.state]);

  useEffect(() => {
    if (jobId) fetchRegistrations();
  }, [jobId]);

  /* =============================================
     FETCH
  ============================================= */
  const fetchRegistrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await JobFairAPI.getJobFairRegistrations(jobId);
      const data = response?.data;

      const studentList = Array.isArray(data?.student_registrations)
        ? data.student_registrations
        : [];

      const hrList = Array.isArray(data?.hr_registrations)
        ? data.hr_registrations
        : [];

      const mappedStudents = studentList.map((item, index) => ({
        id: `stu-${item?.id ?? index}`,
        realId: item?.id ?? null,
        name: item?.full_name || "N/A",
        email: item?.email || "N/A",
        phone: item?.phone || "N/A",
        nhrcid: item?.nhrc_id || "N/A",
        status: item?.status || "Pending",
        location: item?.location || "N/A",
        member_type: "student",
        college: item?.college_name || "N/A",
        year_of_passout: item?.year_of_passout || "N/A",
        department: item?.department || "N/A",
        technical_skills: item?.technical_skills || "N/A",
        preferred_job_role: item?.preferred_job_role || "N/A",
        iam_a: item?.iam_a || "Student",
        created_at: item?.created_at || null,
        raw: item,
      }));

      const mappedHr = hrList.map((item, index) => ({
        id: `hr-${item?.id ?? index}`,
        realId: item?.id ?? null,
        name: item?.full_name || item?.company_name || "N/A",
        email: item?.email || "N/A",
        phone: item?.phone || "N/A",
        nhrcid: item?.nhrc_id || "N/A",
        status: item?.status || "Pending",
        location: item?.location || "N/A",
        member_type: "hr",
        college: item?.college_name || item?.company_name || "N/A",
        year_of_passout: item?.year_of_passout || "N/A",
        department: item?.department || "N/A",
        technical_skills: item?.technical_skills || "N/A",
        preferred_job_role: item?.preferred_job_role || "N/A",
        iam_a: item?.iam_a || "HR",
        created_at: item?.created_at || null,
        raw: item,
      }));

      setCandidates([...mappedStudents, ...mappedHr]);
      setSelectedIds([]);
    } catch (err) {
      console.error("Failed to fetch registrations:", err);
      setError("Failed to load registrations. Please try again.");
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  /* =============================================
     COUNTS
  ============================================= */
  const countStudents = useMemo(
    () => candidates.filter((c) => c.member_type === "student").length,
    [candidates]
  );
  const countHr = useMemo(
    () => candidates.filter((c) => c.member_type === "hr").length,
    [candidates]
  );

  /* =============================================
     APPROVE
  ============================================= */
  const handleApprove = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one candidate to approve.");
      return;
    }
    try {
      await Promise.all(
        selectedIds.map((id) => {
          const candidate = candidates.find((c) => c.id === id);
          if (!candidate?.realId) return Promise.resolve();
          if (candidate.member_type === "hr") {
            return JobFairAPI.approveHrRegistration(candidate.realId);
          } else {
            return JobFairAPI.approveStudentRegistration(candidate.realId);
          }
        })
      );
      await fetchRegistrations();
      alert("Approved successfully.");
    } catch (err) {
      console.error("Approve failed:", err);
      alert("Failed to approve. Please try again.");
    }
  };

  /* =============================================
     REJECT
  ============================================= */
  const handleReject = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one candidate to reject.");
      return;
    }
    try {
      await Promise.all(
        selectedIds.map((id) => {
          const candidate = candidates.find((c) => c.id === id);
          if (!candidate?.realId) return Promise.resolve();
          if (candidate.member_type === "hr") {
            return JobFairAPI.rejectHrRegistration(candidate.realId);
          } else {
            return JobFairAPI.rejectStudentRegistration(candidate.realId);
          }
        })
      );
      await fetchRegistrations();
      alert("Rejected successfully.");
    } catch (err) {
      console.error("Reject failed:", err);
      alert("Failed to reject. Please try again.");
    }
  };

  /* =============================================
     DELETE
  ============================================= */
  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      alert("Please select candidate(s) to delete");
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} candidate(s)?`)) return;
    try {
      await Promise.all(
        selectedIds.map((id) => {
          const candidate = candidates.find((c) => c.id === id);
          if (!candidate?.realId) return Promise.resolve();
          return ApiService.RegistrationAPI.deleteRegistration(candidate.realId);
        })
      );
      await fetchRegistrations();
      setPage(1);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete. Please try again.");
    }
  };

  /* =============================================
     FILTER + PAGINATION
  ============================================= */
  const filteredAll = useMemo(
    () =>
      candidates.filter((c) => {
        if (memberTypeFilter !== "all" && c.member_type !== memberTypeFilter)
          return false;
        if (statusFilter !== "all" && c.status.toLowerCase() !== statusFilter)
          return false;
        if (nameFilter && !c.name.toLowerCase().includes(nameFilter.toLowerCase()))
          return false;
        if (nhrcFilter && !c.nhrcid.toLowerCase().includes(nhrcFilter.toLowerCase()))
          return false;
        if (emailFilter && !c.email.toLowerCase().includes(emailFilter.toLowerCase()))
          return false;
        return true;
      }),
    [candidates, statusFilter, memberTypeFilter, nameFilter, nhrcFilter, emailFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredAll.length / pageSize));

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAll.slice(start, start + pageSize);
  }, [filteredAll, page]);

  /* =============================================
     CHECKBOX LOGIC
  ============================================= */
  const pagedIds = useMemo(() => paged.map((c) => c.id), [paged]);

  const selectedOnPage = useMemo(
    () => pagedIds.filter((id) => selectedIds.includes(id)),
    [pagedIds, selectedIds]
  );

  const allPageSelected =
    pagedIds.length > 0 && selectedOnPage.length === pagedIds.length;
  const somePageSelected = selectedOnPage.length > 0 && !allPageSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePageSelected;
    }
  }, [somePageSelected]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pagedIds.forEach((id) => next.add(id));
        return Array.from(next);
      });
    } else {
      setSelectedIds((prev) => prev.filter((id) => !pagedIds.includes(id)));
    }
  };

  const handleRowCheckbox = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* =============================================
     HELPERS
  ============================================= */
  const getStatusBadgeClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved" || s === "shortlisted")
      return "border border-success text-success bg-transparent";
    if (s === "rejected")
      return "border border-danger text-danger bg-transparent";
    if (s === "pending")
      return "border border-warning text-warning bg-transparent";
    return "border border-secondary text-secondary bg-transparent";
  };

  const getMemberTypeBadgeClass = (type) => {
    if (type === "student") return "bg-primary";
    if (type === "hr") return "bg-success";
    return "bg-secondary";
  };

  const getMemberTypeLabel = (type) => {
    if (type === "student") return "Student";
    if (type === "hr") return "Company / HR";
    return type || "N/A";
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setMemberTypeFilter("all");
    setNameFilter("");
    setNhrcFilter("");
    setEmailFilter("");
    setPage(1);
  };

  const handleViewCandidate = (id) => {
    const candidate = candidates.find((c) => c.id === id);
    if (!candidate) return;
    setSelectedCandidate(candidate);
    setShowPopup(true);
  };

  const handleDownloadSelected = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one candidate to download.");
      return;
    }
    const selected = candidates.filter((c) => selectedIds.includes(c.id));
    const doc = new Document({
      sections: selected.map((c) => ({
        properties: {},
        children: [
          new Paragraph({
            children: [new TextRun({ text: "Candidate Details", bold: true, size: 28 })],
          }),
          new Paragraph(""),
          new Paragraph(`Name: ${c.name}`),
          new Paragraph(`Email: ${c.email}`),
          new Paragraph(`Phone: ${c.phone}`),
          new Paragraph(`NHRC ID: ${c.nhrcid}`),
          new Paragraph(`Member Type: ${getMemberTypeLabel(c.member_type)}`),
          new Paragraph(`Status: ${c.status}`),
          new Paragraph(`Location: ${c.location}`),
          new Paragraph(`College: ${c.college}`),
          new Paragraph(`Department: ${c.department}`),
          new Paragraph(`Year of Passout: ${c.year_of_passout}`),
          new Paragraph(`Preferred Role: ${c.preferred_job_role}`),
          new Paragraph(`Technical Skills: ${c.technical_skills}`),
          new Paragraph(""),
          new Paragraph("------------------------------------------------------"),
          new Paragraph(""),
        ],
      })),
    });
    try {
      const blob = await Packer.toBlob(doc);
      const fileName = `candidates_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}.docx`;
      saveAs(blob, fileName);
    } catch (err) {
      console.error("Download error:", err);
      alert("Failed to download.");
    }
  };

  /* =============================================
     RENDER
  ============================================= */
  return (
    <div className="container-fluid py-3 mt-5">
      <h3 className="training-title mt-2 fw-bold" style={{ color: "#8D0000" }}>
        JOBS & JOB FAIRS
      </h3>

      {/* TOP BAR */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <button
            className="btn btn-outline-secondary btn-sm mb-2"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft size={18} /> Back to Jobs
          </button>
          <h5 className="mb-0">Registrations</h5>
          <small className="text-muted">
            Event: <span className="fw-semibold">{jobTitle}</span>
          </small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button variant="success" size="sm" onClick={handleApprove}>
            <SiTicktick /> Approve
          </Button>
          <Button variant="outline-danger" size="sm" onClick={handleReject}>
            <ImConfused /> Reject
          </Button>
          <Button variant="primary" size="sm">
            <HiOutlineMail /> Email
          </Button>
          <Button variant="secondary" size="sm">
            <RiShareForwardLine /> Forward
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={handleDownloadSelected}>
            <RiDownloadFill /> Download
          </Button>
          <Button variant="outline-danger" size="sm" onClick={handleDelete}>
            <MdOutlineDeleteOutline /> Delete
          </Button>
        </div>
      </div>

      {/* COUNT CARDS */}
      {!loading && !error && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-4">
            <div
              className="card border-0 shadow-sm text-center p-3"
              style={{ cursor: "pointer", borderLeft: "4px solid #6c757d" }}
              onClick={() => { setMemberTypeFilter("all"); setPage(1); }}
            >
              <div className="fs-3 fw-bold text-secondary">{candidates.length}</div>
              <div className="text-muted small">Total Registrations</div>
            </div>
          </div>
          <div className="col-6 col-md-4">
            <div
              className="card border-0 shadow-sm text-center p-3"
              style={{ cursor: "pointer", borderLeft: "4px solid #0d6efd" }}
              onClick={() => { setMemberTypeFilter("student"); setPage(1); }}
            >
              <div className="fs-3 fw-bold text-primary">{countStudents}</div>
              <div className="text-muted small">Students</div>
            </div>
          </div>
          <div className="col-6 col-md-4">
            <div
              className="card border-0 shadow-sm text-center p-3"
              style={{ cursor: "pointer", borderLeft: "4px solid #198754" }}
              onClick={() => { setMemberTypeFilter("hr"); setPage(1); }}
            >
              <div className="fs-3 fw-bold text-success">{countHr}</div>
              <div className="text-muted small">Companies / HR</div>
            </div>
          </div>
        </div>
      )}

      {/* FILTERS */}
      <div className="bg-light border rounded-3 p-3 mb-3">
        <div className="row g-3 align-items-end">
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="all">All</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Member Type</label>
            <select
              className="form-select"
              value={memberTypeFilter}
              onChange={(e) => { setMemberTypeFilter(e.target.value); setPage(1); }}
            >
              <option value="all">All</option>
              <option value="student">Student</option>
              <option value="hr">Company / HR</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name"
              value={nameFilter}
              onChange={(e) => { setNameFilter(e.target.value); setPage(1); }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              placeholder="Search by email"
              value={emailFilter}
              onChange={(e) => { setEmailFilter(e.target.value); setPage(1); }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">NHRC ID</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by NHRC ID"
              value={nhrcFilter}
              onChange={(e) => { setNhrcFilter(e.target.value); setPage(1); }}
            />
          </div>
          <div className="col-md-2 d-flex justify-content-end">
            <button className="btn btn-outline-secondary w-100" onClick={resetFilters}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2 text-muted">Loading registrations...</p>
        </div>
      )}

      {/* ERROR */}
      {error && !loading && (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <span>{error}</span>
          <button className="btn btn-sm btn-outline-danger" onClick={fetchRegistrations}>
            Retry
          </button>
        </div>
      )}

      {/* STATUS BADGES */}
      {!loading && !error && (
        <div className="d-flex gap-3 mb-3 flex-wrap">
          <span className="badge bg-secondary fs-6">Showing: {filteredAll.length}</span>
          <span className="badge bg-success fs-6">
            Approved: {candidates.filter((c) => c.status.toLowerCase() === "approved").length}
          </span>
          <span className="badge bg-danger fs-6">
            Rejected: {candidates.filter((c) => c.status.toLowerCase() === "rejected").length}
          </span>
          <span className="badge bg-warning text-dark fs-6">
            Pending: {candidates.filter((c) => c.status.toLowerCase() === "pending").length}
          </span>
          {selectedIds.length > 0 && (
            <span className="badge bg-info text-dark fs-6">
              Selected: {selectedIds.length}
            </span>
          )}
        </div>
      )}

      {/* TABLE */}
      {!loading && (
        <>
          {/* HEADER ROW */}
          <div
            className="border-bottom pb-2 mb-2"
            style={{ display: "grid", gridTemplateColumns: "40px 1fr", alignItems: "center" }}
          >
            <div>
              <input
                type="checkbox"
                ref={selectAllRef}
                style={{ transform: "scale(0.9)", accentColor: "#0d6efd", cursor: "pointer" }}
                checked={allPageSelected}
                onChange={handleSelectAll}
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 2fr 1.5fr 1fr 1.5fr 1.5fr 1fr",
                gap: "8px",
              }}
              className="text-muted small fw-semibold"
            >
              <div>Name</div>
              <div>Email</div>
              <div>Phone</div>
              <div>NHRC ID</div>
              <div>Type</div>
              <div>Status</div>
              <div className="text-center">Actions</div>
            </div>
          </div>

          {/* DATA ROWS */}
          {paged.map((c) => (
            <div
              key={c.id}
              className="border-bottom py-2"
              style={{ display: "grid", gridTemplateColumns: "40px 1fr", alignItems: "center" }}
            >
              <div>
                <input
                  type="checkbox"
                  style={{ transform: "scale(0.9)", accentColor: "#0d6efd", cursor: "pointer" }}
                  checked={selectedIds.includes(c.id)}
                  onChange={() => handleRowCheckbox(c.id)}
                />
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 2fr 1.5fr 1fr 1.5fr 1.5fr 1fr",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <div className="fw-semibold text-truncate" title={c.name}>
                  {c.name}
                </div>
                <div>
                  <small className="text-break">{c.email}</small>
                </div>
                <div>
                  <small>{c.phone}</small>
                </div>
                <div>
                  <small>{c.nhrcid}</small>
                </div>
                <div>
                  <span
                    className={`badge ${getMemberTypeBadgeClass(c.member_type)}`}
                    style={{ fontSize: "0.78rem" }}
                  >
                    {getMemberTypeLabel(c.member_type)}
                  </span>
                </div>
                <div>
                  <span
                    className={`badge ${getStatusBadgeClass(c.status)}`}
                    style={{
                      fontSize: "0.82rem",
                      minWidth: "80px",
                      padding: "0.35rem 0.5rem",
                      display: "inline-block",
                      textAlign: "center",
                    }}
                  >
                    {c.status}
                  </span>
                </div>
                <div className="text-center">
                  <button
                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                    onClick={() => handleViewCandidate(c.id)}
                  >
                    <Eye size={16} />
                    <span className="d-none d-md-inline">View</span>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {paged.length === 0 && (
            <p className="text-muted mt-3">No candidates match the filters.</p>
          )}

          {/* PAGINATION */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              Showing {filteredAll.length === 0 ? 0 : (page - 1) * pageSize + 1}–
              {Math.min(page * pageSize, filteredAll.length)} of {filteredAll.length}
            </small>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-primary"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <small className="text-muted">
                Page {page} of {totalPages}
              </small>
              <button
                className="btn btn-sm btn-primary"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* POPUP */}
      <div className={`popup-form-overlay ${showPopup ? "show" : ""}`}>
        <div className="popup-form">
          <div className="popup-header">
            <h3>Candidate Details</h3>
            <button className="close-btn-res" onClick={() => setShowPopup(false)}>
              &times;
            </button>
          </div>
          <form className="stu-live-pg-register-card">
            <div className="stu-live-pg-register-grid">
              <div className="stu-live-pg-register-field">
                <label>Full Name</label>
                <input readOnly value={selectedCandidate?.name || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>Mobile Number</label>
                <input readOnly value={selectedCandidate?.phone || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>Email</label>
                <input type="email" readOnly value={selectedCandidate?.email || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>Location</label>
                <input readOnly value={selectedCandidate?.location || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>Member Type</label>
                <input readOnly value={getMemberTypeLabel(selectedCandidate?.member_type)} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>NHRC ID</label>
                <input readOnly value={selectedCandidate?.nhrcid || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>College Name</label>
                <input readOnly value={selectedCandidate?.college || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>Department</label>
                <input readOnly value={selectedCandidate?.department || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>Year of Passout</label>
                <input readOnly value={selectedCandidate?.year_of_passout || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>Preferred Job Role</label>
                <input readOnly value={selectedCandidate?.preferred_job_role || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>Technical Skills</label>
                <input readOnly value={selectedCandidate?.technical_skills || ""} />
              </div>
              <div className="stu-live-pg-register-field">
                <label>Status</label>
                <input readOnly value={selectedCandidate?.status || ""} />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default JfTotalApplicationsRes;